import { useMutation, useQueryClient } from "@tanstack/react-query";
import { templateBuilderMutations } from "@/features/template-builder/mutations";
import { templateBuilderQueries } from "@/features/template-builder/queries";
import { insertCachedTemplateStep } from "@/features/template-builder/queries/cache";
import type { StepWithSettings } from "@/domains/templates/types";
import type {
  CreateSubanalysisStepInput,
  SubanalysisDefinitionInput,
} from "@/domains/templates/subanalysis.types";

export function useSaveSubanalysis(
  templateId: string,
  stepId?: string,
  expectedRevision?: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      draft: CreateSubanalysisStepInput | SubanalysisDefinitionInput,
    ) => {
      const definition: SubanalysisDefinitionInput = {
        call: draft.call,
        inputs: draft.inputs,
      };
      if (stepId) {
        const currentStep = queryClient
          .getQueryData<StepWithSettings[]>(
            templateBuilderQueries.steps(templateId).queryKey,
          )
          ?.find((step) => step.analysis_step_id === stepId);
        if (!currentStep?.record_updated)
          throw new Error(
            "Reload this section before saving its configuration.",
          );
        return templateBuilderMutations.updateSubanalysisStep({
          templateId,
          stepId,
          definition: {
            ...definition,
            expected_record_updated:
              expectedRevision ?? currentStep.record_updated,
          },
        });
      }
      if (!("display_name" in draft) || !draft.display_name.trim())
        throw new Error("A section name is required.");
      if (!("placement" in draft))
        throw new Error("Choose where to insert this section.");
      return templateBuilderMutations.createSubanalysisStep({
        templateId,
        definition: {
          ...definition,
          display_name: draft.display_name.trim(),
          placement: draft.placement,
        },
      });
    },
    onError: async (error, draft) => {
      if (
        typeof error === "object" &&
        error &&
        "status" in error &&
        error.status === 409
      ) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: templateBuilderQueries.steps(templateId).queryKey,
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: templateBuilderQueries.detail(
              draft.call.binding_template_id,
            ).queryKey,
            exact: true,
          }),
          queryClient.invalidateQueries({
            queryKey: templateBuilderQueries.detail(templateId).queryKey,
            exact: true,
          }),
        ]);
      }
    },
    onSuccess: (canonicalStep) => {
      if (!stepId) {
        insertCachedTemplateStep(queryClient, templateId, canonicalStep);
        return;
      }
      queryClient.setQueryData<StepWithSettings[]>(
        templateBuilderQueries.steps(templateId).queryKey,
        (current) =>
          current?.map((step) =>
            step.analysis_step_id === canonicalStep.analysis_step_id
              ? canonicalStep
              : step,
          ),
      );
    },
  });
}
